import React, { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import { presignUpload, putToBucket, saveMetadata, deleteRegisterRequestUpload } from './registerUploadApi';
import { RegisterRequestMinimalInterface } from './types';

type RowStatus = 'uploading' | 'ok' | 'error';

type UploadedRow = {
  localKey: string;
  id?: number;
  name: string;
  size: number;
  mimeType: string;
  status: RowStatus;
  progress: number;
  errorText?: string;
  url?: string;
};

function formatMB(bytes: number) {
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

function iconFor(name: string) {
  const lower = name.toLowerCase();
  if (lower.endsWith('.pdf')) return PictureAsPdfOutlinedIcon;
  if (/\.(jpg|jpeg|png|heic|avif)$/i.test(lower)) return ImageOutlinedIcon;
  return InsertDriveFileOutlinedIcon;
}

export type FileUploadHandle = {
  deleteAllUploadedFiles: () => Promise<void>;
  hasUploadedFiles: () => boolean;
};

type FileUploadProps = {
  ongoingRegisterRequest: RegisterRequestMinimalInterface;
  onSuccessChange?: (hasSuccessfulUpload: boolean) => void;
};

function FileUploadComponent(
  { ongoingRegisterRequest, onSuccessChange }: FileUploadProps,
  ref: React.ForwardedRef<FileUploadHandle>
) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [rows, setRows] = useState<UploadedRow[]>([]);
  const [busy, setBusy] = useState(false);

  const hasSuccessfulUpload = rows.some((row) => row.status === 'ok');

  useImperativeHandle(ref, () => ({
    hasUploadedFiles: () => rows.some((row) => row.status === 'ok' && row.id),

    deleteAllUploadedFiles: async () => {
      const uploadedRows = rows.filter((row) => row.status === 'ok' && row.id);

      await Promise.all(uploadedRows.map((row) => deleteRegisterRequestUpload(ongoingRegisterRequest.id, row.id!)));

      setRows((prev) => prev.filter((row) => !(row.status === 'ok' && row.id)));
    },
  }));

  useEffect(() => {
    onSuccessChange?.(hasSuccessfulUpload);
  }, [hasSuccessfulUpload, onSuccessChange]);

  async function handlePick(file: File) {
    if (busy) return;
    setBusy(true);

    const tempKey = `${file.name}-${file.size}-${Date.now()}`;

    const newRow: UploadedRow = {
      localKey: tempKey,
      name: file.name,
      size: file.size,
      mimeType: file.type,
      status: 'uploading',
      progress: 0,
    };

    setRows((prev) => [...prev, newRow]);

    try {
      const presigned = await presignUpload(ongoingRegisterRequest.id, file);

      await putToBucket(presigned.upload.url, presigned.upload.headers, file, (pct) => {
        setRows((prev) => {
          const copy = [...prev];
          const idx = copy.findIndex((r) => r.localKey === tempKey);
          if (idx >= 0) copy[idx] = { ...copy[idx], progress: pct };
          return copy;
        });
      });

      const saved = await saveMetadata(ongoingRegisterRequest.id, presigned.upload.shrine);

      setRows((prev) => {
        const copy = [...prev];
        const idx = copy.findIndex((r) => r.localKey === tempKey);
        if (idx >= 0) {
          copy[idx] = {
            ...copy[idx],
            id: saved.id,
            status: 'ok',
            progress: 100,
            url: saved.url,
          };
        }
        return copy;
      });
    } catch (e: any) {
      // Try Rails payload first: { error: 'validation_failed', messages: [...] }
      const apiMessages = e?.response?.data?.messages;
      const firstApiMessage = Array.isArray(apiMessages) && apiMessages.length > 0 ? apiMessages[0] : null;

      // Fallbacks
      const fallbackMessage = typeof e?.message === 'string' && e.message.length > 0 ? e.message : 'Upload failed';

      const message = firstApiMessage ?? fallbackMessage;

      setRows((prev) => {
        const copy = [...prev];
        const idx = copy.findIndex((r) => r.localKey === tempKey);
        if (idx >= 0) {
          copy[idx] = {
            ...copy[idx],
            status: 'error',
            errorText: message,
          };
        }
        return copy;
      });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function removeRow(index: number) {
    const row = rows[index];
    if (row.status === 'uploading') return;

    if (row.id) {
      try {
        await deleteRegisterRequestUpload(ongoingRegisterRequest.id, row.id);
      } catch {
        return;
      }
    }

    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  return (
    <>
      <div className="w-full max-w-2xl">
        <label
          htmlFor={inputId}
          className={`
          relative block select-none rounded-[20px] bg-white p-0 text-center
          ${busy ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}
        `}
        >
          <span className="pointer-events-none absolute inset-0">
            <svg className="h-full w-full" aria-hidden="true">
              <rect
                x="2"
                y="2"
                width="calc(100% - 4px)"
                height="calc(100% - 4px)"
                rx="20"
                ry="20"
                fill="none"
                stroke="currentColor"
                className="text-[#439676]"
                strokeWidth="2"
                strokeDasharray="18 14"
              />
            </svg>
          </span>

          <div className="p-4">
            <CloudUploadIcon className="mx-auto mb-4 !text-[40px] text-[#439676]" />

            <div className="text-sm sm:text-lg font-bold">{busy ? 'Laddar upp...' : 'Välj fil att ladda upp'}</div>

            <div className="mx-auto mt-3 max-w-lg text-xs sm:text-sm leading-snug text-neutral-600">
              Godkända filformat: JPEG, PNG, HEIC,
              <br />
              AVIF &amp; PDF (max 20mb per fil)
            </div>

            <div
              className={`
              mx-auto mt-6 inline-flex items-center gap-3
              rounded-full px-8 sm:px-10 py-1 sm:py-2
              text-sm sm:text-lg font-bold uppercase tracking-wide text-white
              shadow-[0_10px_22px_rgba(67,150,118,0.28)]
              ${busy ? 'bg-neutral-400' : 'bg-[#439676]'}
            `}
            >
              {busy ? 'LADDAR UPP...' : 'VÄLJ FIL'}
              <CloudUploadIcon className="!text-[22px]" />
            </div>
          </div>

          <input
            id={inputId}
            ref={inputRef}
            type="file"
            className="hidden"
            accept=".jpg,.jpeg,.png,.heic,.avif,.pdf,.txt"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handlePick(file);
            }}
          />
        </label>

        <div className="my-6 space-y-4">
          {rows.map((row, idx) => (
            <FileRow key={row.localKey} row={row} onRemove={() => removeRow(idx)} />
          ))}
        </div>
      </div>
    </>
  );
}

function FileRow({ row, onRemove }: { row: UploadedRow; onRemove: () => void }) {
  const Icon = iconFor(row.name);

  const ring =
    row.status === 'ok'
      ? 'ring-2 ring-[#439676]'
      : row.status === 'error'
      ? 'ring-2 ring-[#E4717C]'
      : 'ring-2 ring-neutral-400';

  const iconColor =
    row.status === 'ok' ? 'text-[#439676]' : row.status === 'error' ? 'text-[#E4717C]' : 'text-neutral-500';

  return (
    <div
      className={`
        flex items-center justify-between gap-4
        rounded-2xl bg-white px-4 py-3
        ${ring}
      `}
    >
      <div className="flex min-w-0 items-center gap-3">
        <Icon className={`${iconColor} !text-[26px]`} />
        <div className="min-w-0">
          <div className="truncate text-base text-neutral-700">
            {row.name} ({formatMB(row.size)})
          </div>

          {row.status === 'uploading' ? (
            <div className="mt-1 h-2 w-full overflow-hidden rounded bg-neutral-200">
              <div className="h-full rounded bg-[#439676] transition-all" style={{ width: `${row.progress}%` }} />
            </div>
          ) : null}

          {row.status === 'error' && row.errorText ? (
            <div className="mt-1 text-sm font-semibold text-[#E4717C]">{row.errorText}</div>
          ) : null}
        </div>
      </div>

      {row.status === 'uploading' ? null : row.status === 'error' ? (
        <button
          type="button"
          onClick={onRemove}
          className="rounded-md p-2 text-[#E4717C] hover:bg-red-50"
          aria-label="Remove file"
        >
          <DeleteOutlineOutlinedIcon />
        </button>
      ) : (
        <button
          type="button"
          onClick={onRemove}
          className="rounded-md p-2 text-[#439676] hover:bg-emerald-50"
          aria-label="Remove file"
        >
          <DeleteOutlineOutlinedIcon />
        </button>
      )}
    </div>
  );
}

export const FileUpload = forwardRef(FileUploadComponent);
